'use client';

import { FONT_TEXT } from '@/app/fonts';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ShareableChart } from '../ShareableChart';

const data = [
  { name: 'Organic/SEO', low: 300, high: 800, range: '$300–$800', color: '#10b981' },
  { name: 'First-Party Exclusive', low: 1000, high: 3000, range: '$1,000–$3,000', color: '#ff34a7' },
  { name: 'Premium Aggregator', low: 2500, high: 4500, range: '$2,500–$4,500', color: '#94a3b8' },
  { name: 'Shared Aggregator', low: 5000, high: 10000, range: '$5,000–$10,000+', color: '#94a3b8' },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: typeof data[0] }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
      <p className="font-semibold text-sm text-gray-900">{item.name}</p>
      <p className="text-sm text-gray-600">
        CPFL: <span className="font-medium">{item.range}</span>
      </p>
    </div>
  );
}

interface CustomBarLabelProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
}

function CustomBarLabel({ x = 0, y = 0, width = 0, height = 0, index = 0 }: CustomBarLabelProps) {
  const item = data[index];
  if (!item) return null;
  
  return (
    <text
      x={x + width + 8}
      y={y + height / 2}
      fill="#374151"
      fontSize={12}
      fontWeight={500}
      dominantBaseline="middle"
      fontFamily={FONT_TEXT}
    >
      {item.range}
    </text>
  );
}

export function CPFLComparisonChart() {
  return (
    <ShareableChart title="Cost Per Funded Loan by Source 2026">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: FONT_TEXT }}>
          Cost Per Funded Loan by Source (2026)
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          The metric that actually matters
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4" style={{ fontFamily: FONT_TEXT }}>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#10b981' }} />
          <span className="text-xs text-gray-600">Organic / SEO</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#ff34a7' }} />
          <span className="text-xs text-gray-600">First-Party Exclusive (leadpops)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#94a3b8' }} />
          <span className="text-xs text-gray-600">Third-Party Aggregator</span>
        </div>
      </div>

      {/* Insight callout */}
      <div className="mb-4 p-3 rounded-lg border-l-4" style={{ borderColor: '#ff34a7', backgroundColor: '#fff5fb' }}>
        <p className="text-sm text-gray-700">
          <strong>Key insight:</strong> The conversion rates between Bankrate and first-party paid aren't dramatically different (both 3-5%). 
          The real differentiator is CPL: <span style={{ color: '#ff34a7', fontWeight: 600 }}>$50 vs $150</span>. That gap compounds fast.
        </p>
      </div>

      <div className="w-full" style={{ height: '260px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 5, right: 120, left: 10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 11000]}
              tick={{ fontSize: 11, fill: '#6b7280', fontFamily: FONT_TEXT }}
              tickFormatter={(value: number) => value >= 1000 ? `$${value / 1000}k` : `$${value}`}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={150}
              tick={{ fontSize: 12, fill: '#374151', fontFamily: FONT_TEXT }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
            <Bar dataKey="high" radius={[0, 4, 4, 0]} barSize={32} label={<CustomBarLabel />}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <p className="text-xs text-gray-400 mt-3" style={{ fontFamily: FONT_TEXT }}>
        Source: leadpops.com · Industry data, HousingWire (2025–2026)
      </p>
    </ShareableChart>
  );
}
